import React, { useCallback, useRef, useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { getNavbarConfig } from '../services/endpoints/general';
import { tryToParseToJson } from './helper';
import { store } from '../store';

let connectCount = 0;
let socketTimeout = null;

export const WebsocketContext = createContext({});

export const WebsocketProvider = ({ children }) => {
  const socket = useRef(null);

  const [socketsInit, setSocketsInit] = useState(false);
  const [eventData, setEventData] = useState({});

  const getNavbar = async () => {
    try {
      const response = await getNavbarConfig();
      return response.data;
    } catch (error) {
      console.log(error);
      return error;
    }
  };

  const onClose = () => {
    connectCount = 0;
    setEventData({});
    clearTimeout(socketTimeout);
    socket?.current?.close();
  };

  const initWebsockets = useCallback((websocketUrl, onMessage) => {
    try {
      socket.current = new WebSocket(websocketUrl || window?.appConfig?.websocket_url);
    } catch (error) {
      setEventData({});
      setSocketsInit(false);
      socket.current = null;
      return error;
    }

    socket.current.onerror = (event) => {
      console.log(event);
      setEventData({});
      setSocketsInit(false);
    };

    socket.current.onopen = () => {
      setSocketsInit(true);
      connectCount = 0;
    };

    socket.current.onmessage = (event) => {
      const data = tryToParseToJson(event?.data);
      if (data) {
        setEventData(data);
        onMessage?.(data);
      }
    };

    socket.current.onclose = async (event) => {
      setEventData({});
      socketTimeout = setTimeout(async () => {
        const { user } = store.getState();
        if (user.isAuth) {
          try {
            const { websocket_url, reserve_websocket_url } = await getNavbar();
            let newWebsocketUrl = websocket_url;
            if (!newWebsocketUrl) {
              return;
            }
            if (connectCount >= 1) {
              newWebsocketUrl = reserve_websocket_url;
            }
            if (connectCount >= 2) {
              Sentry.captureException(`Websocket: fail_connect, url: ${newWebsocketUrl}`, event);
              return;
            }
            connectCount += 1;
            console.log('The connection has been closed successfully.', event);
            initWebsockets(newWebsocketUrl);
          } catch (error) {
            if (error?.status === 401) {
              socketTimeout = null;
              clearTimeout(socketTimeout);
              return;
            }
            Sentry.captureException('Websocket: get navbar error', event);
          }
        }
      }, 15000);
    };
    return socket.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      connectCount = 0;
      setEventData({});
      socketTimeout = null;
      clearTimeout(socketTimeout);
      socket?.current?.close();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <WebsocketContext.Provider value={{ initWebsockets, socketsInit, socket: socket.current, eventData, onClose }}>
      {children}
    </WebsocketContext.Provider>
  );
};

WebsocketProvider.propTypes = {
  children: PropTypes.object,
};
